import { DashboardIntegrationType } from "@acumen/dashboard-common";
import classNames from "classnames";
import { useStores } from "../../../../mobx-stores";
import { CONNECT_BUTTON_TEXT, IIntegrationFlow, NEW_INTEGRATION_URL, StepsActionType } from "../../../../mobx-stores/new-integrations-store";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Icon, Modal, Step } from "semantic-ui-react";
import ChooseIntegration from "./choose-integration";
import "./style.scss";

interface IIntegrationFlowStep {
	currentStepNumber: number;
	onNext: (integration?: string | DashboardIntegrationType) => void;
	message?: { msg: string, isError: boolean };
	onClose: () => void;
	userText?: string;
	installLink?: string;
}

const BIG_BUTTON_STYLE = false;

// tslint:disable-next-line: variable-name
const IntegrationFlowStep = (props: IIntegrationFlowStep) => {
	const { integrationsStore: {
		currentIntegrationFlow,
		currentIntegrationType,
		setIntegrationType
	} } = useStores();
	const [open, setOpen] = useState(true);
	const [isStepDone, setStepDone] = useState(false);
	const [isLoading, toggleLoading] = useState(false);
	const { currentStepNumber, onNext, message, onClose, userText } = { ...props };
	const currentStep = currentIntegrationFlow?.steps[currentStepNumber] ?? currentIntegrationFlow?.steps[0];
	const [userInput, setUserInput] = useState<string | undefined>(undefined);
	const history = useHistory();

	useEffect(() => {
		if (currentStep && currentStep.actionType === StepsActionType.FetchInstallData && !isLoading) {
			toggleLoading(true);
			onNext();
		}
	}, [currentStepNumber, currentIntegrationFlow]);

	const flowStepsHeader = (completeFlow: IIntegrationFlow) => {
		const { iconSrc, name, steps, length } = { ...completeFlow };
		return (
			<Step.Group widths={length}>
				<Step className="steps-header">
					<span className="title-icon">
						<img src={iconSrc} alt={""} />
					</span>
					<Step.Content>
						<Step.Title>{name}</Step.Title>
					</Step.Content>
				</Step>
				{steps.map((step, i) => (
					step.isVisible &&
					<Step
						key={i}
						className={classNames({
							disabled: i > currentStepNumber,
							completed: (i < currentStepNumber || (i === currentStepNumber && isStepDone)),
							active: i === currentStepNumber
						})}
					>
						<Icon />
						<Step.Content>
							<Step.Title style={{ fontWeight: "inherit" }}>{step.name} </Step.Title>
						</Step.Content>
					</Step>
				))}
			</Step.Group>
		);
	};

	const getActionBox = () => {
		switch (currentStep?.actionType) {
			case StepsActionType.FetchInstallData:
				return BIG_BUTTON_STYLE ? (
					<div className={"big-button-style"}>
						<div className="ui huge buttons">
							<button className="ui button loading" />
							<button className="ui button">{CONNECT_BUTTON_TEXT}</button>
						</div>
					</div>
				) : <></>;
			case StepsActionType.GoToRedirect:
				return BIG_BUTTON_STYLE ? (
					<div className={"big-button-style"}>
						<button className="ui primary button huge"
							onClick={() => onNext()}>
							{CONNECT_BUTTON_TEXT}
						</button>
					</div>
				) : <>
					{(currentIntegrationType === DashboardIntegrationType.JIRACloud ||
						currentIntegrationType === DashboardIntegrationType.JIRAServer) && userText &&
						<div className="ui buttons copy-input">
							<input className="ui label break-word" value={userText} disabled={true} width="200px" />
							<button className="ui primary button"
								onClick={() => navigator.clipboard.writeText(userText)}>
								<i className="ui icon copy outline" aria-hidden="true" />
									Copy
								</button>
						</div>
					}
				</>;
			case StepsActionType.UserInput:
				const { placeholder, type } = { ...currentStep.userInput };
				return (
					<div className="flex">
						<form className="ui form">
							<div className="field">
								<div className="ui input">
									<input
										required={true}
										placeholder={placeholder}
										type={type}
										onChange={(e) => {
											setUserInput(e.target.value ?? undefined);
										}}
									/>
									<span style={{ padding: "0px 10px" }} />
									<button
										className="ui button"
										onClick={(e) => { e.preventDefault(); onNext(userInput); }}
										disabled={!userInput}
									>Go</button>
								</div>
								{message?.isError && <div className="ui pointing red label">{message?.msg}</div>}
							</div>
						</form>
					</div>
				);
			case StepsActionType.VerifyLink:
				return (
					<div className={"big-button-style"}>

						<button className="ui primary button huge"
							onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNext(); }}>
							Verify
						</button>
					</div>
				);
			case StepsActionType.GetToken:
				return (
					(currentIntegrationType === DashboardIntegrationType.GitHub ||
						currentIntegrationType === DashboardIntegrationType.Slack) &&
					<form className="ui form">
						<div className="field">
							<div className="ui input">
								<input
									required={true}
									onChange={(e) => {
										setUserInput(e.target.value ?? undefined);
									}}
								/>
								<span style={{ padding: "0px 10px" }} />
								<button
									className="ui button"
									onClick={(e) => {
										e.preventDefault();
										if (!userInput) {
											return;
										}
										try {
											const redirectUrl = new URL(userInput);
											const searchParams = new URLSearchParams(redirectUrl.search);
											if (currentIntegrationType === DashboardIntegrationType.Slack && searchParams && searchParams.has("code")) {
												history.push(`/${NEW_INTEGRATION_URL}/add/setup-slack${redirectUrl.search}`);
											} else if (currentIntegrationType === DashboardIntegrationType.GitHub && searchParams && searchParams.has("installation_id") && searchParams.has("code")) {
												history.push(`/${NEW_INTEGRATION_URL}/add/setup-github${redirectUrl.search}`);
											}
										} catch (e) {
											return;
										}
									}}
									disabled={!userInput}
								>Go</button>
							</div>
						</div>
						<p>If the redirect didn't work <strong>paste the redirect URL you arrived at here</strong></p>
					</form>
				);
			case StepsActionType.ChooseProvider:
				return (
					<ChooseIntegration
						onChoose={(intType) => setIntegrationType(intType)}
					/>
				);
		}
	};

	const getActionButton = () => {
		switch (currentStep?.actionType) {
			case StepsActionType.ChooseProvider:
				return <></>;
			case StepsActionType.FetchInstallData:
				return (
					<div className="ui buttons">
						<button className="ui button loading" disabled={true} />
						<button className="ui button" disabled={true}>{CONNECT_BUTTON_TEXT}</button>
					</div>
				);
			case StepsActionType.UserInput:
				return (
					<></>
				);
			case StepsActionType.GoToRedirect:
				return (
					<button className="ui primary button"
						onClick={() => {
							setStepDone(true);
							onNext();
						}}>
						{CONNECT_BUTTON_TEXT}
					</button>
				);
			case StepsActionType.GetToken:
				return (
					<div className="ui buttons">
						<button className="ui button loading" disabled={true} />
						<button className="ui button" disabled={true}>{CONNECT_BUTTON_TEXT}</button>
					</div>
				);
			case StepsActionType.Success:
				return (
					<button className="ui primary button"
						onClick={(e) => { e.preventDefault(); setStepDone(true); onNext(); history.push(`/${NEW_INTEGRATION_URL}/add`); }}>
						Done
					</button>
				);
		}
	};

	const getMessageBox = () => (
		currentStep?.actionType !== StepsActionType.UserInput ?
			<div className={classNames("ui icon message", { warning: message?.isError })} style={{ position: "relative", bottom: "10%" }}>
				<div className="content">
					<div className="header">
						{message?.isError ? "Something went wrong" : "Notice"}
					</div>
					{message && <p dangerouslySetInnerHTML={{ __html: message.msg }} />}
				</div>
			</div>
			: <></>
	);

	return (
		<Modal
			onClose={() => false}
			onOpen={() => false}
			open={open}
			className={"integration-flow"}
		>
			<Modal.Header style={{ padding: "0px", fontWeight: "inherit" }}>
				{currentIntegrationFlow && flowStepsHeader(currentIntegrationFlow)}
			</Modal.Header>
			{currentStep && <Modal.Content >
				<div className="flex-column description-box" style={{ minHeight: "450px" }}>
					<Modal.Description>
						<h2 className="ui header">{currentStep.name}</h2>
						<p>
							{currentStep.description}
						</p>
						<div className="step-action" style={{ margin: "auto", width: "initial", alignContent: "center" }}>
							{getActionBox()}
						</div>
					</Modal.Description>
					{message && message.isError && getMessageBox()}
				</div>
			</Modal.Content>}
			<Modal.Actions className="step-buttons flex-row">
				<div className="ui buttons">
					<button
						className="ui primary button link-button"
						value="Close"
						onClick={() => {
							onClose();
							setOpen(false);
							history.push(`/${NEW_INTEGRATION_URL}`);
						}}>
						Close</button>
				</div>
				{getActionButton()}
			</Modal.Actions>
		</Modal >
	);
};
export default IntegrationFlowStep;
